import { Box, CircularProgress, Divider, Typography } from '@mui/material'
import { DownwardArrowIcon, InfoIcon, UpwardArrowIcon } from '../../Svgs'
import { formatNumber } from '../../../utils/Common'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import TakeOffTooltip from '../TakeOffTooltip'
import { CostStatus } from '../../../models/TakeOff'
import { Can } from '../../../utils/AbilityUtil'
import {
  AttributeActionNames,
  AttributeResourceNames,
} from '../../../models/AccessControl'

const tooltipText = `All pricing displayed is for general informational purposes only and may not reflect final costs, as actual charges vary based on specific circumstances.`

const ProjectCost = () => {
  const takeOffCalculations = useSelector(
    (state: RootState) => state.takeOff.takeOffCalculations,
  )
  const isTakeOffCalculationInProgress = useSelector(
    (state: RootState) => state.takeOff.isTakeOffCalculationInProgress,
  )

  return (
    <Box
      sx={{
        gap: '4px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: 'calc(400px - 16px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
          alignItems: 'baseline',
        }}
      >
        <Typography
          sx={{
            color: '#06004A',

            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Project Cost
        </Typography>

        <TakeOffTooltip title={tooltipText}>
          <InfoIcon sx={{ color: '#04002E', fontSize: 12 }} />
        </TakeOffTooltip>
      </Box>

      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          borderRadius: '4px 0px 0px 4px',
          backgroundColor: '#F2F2F2',
        }}
      >
        <Can I={AttributeActionNames.Read} a={AttributeResourceNames.TakeOff}>
          {!isTakeOffCalculationInProgress ? (
            <Box
              sx={{
                gap: '10px',
                width: '100%',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  flex: '1 1 0',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '6px',
                      alignItems: 'baseline',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#06004A',

                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: 'normal',
                      }}
                    >
                      Total Build Cost
                    </Typography>

                    <TakeOffTooltip title={tooltipText}>
                      <InfoIcon sx={{ color: '#04002E', fontSize: 10 }} />
                    </TakeOffTooltip>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#06004A',

                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: 'normal',
                      }}
                    >
                      Cost Per Sq Ft
                    </Typography>

                    <Typography
                      sx={{
                        color: '#06004A',

                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: 'normal',
                        textDecorationLine: 'underline',
                      }}
                    >
                      {takeOffCalculations?.costPerSqft
                        ? `$${formatNumber(takeOffCalculations.costPerSqft)}`
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    gap: '4px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'baseline',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#1705E5',

                      fontSize: '40px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: 'normal',
                    }}
                  >
                    {takeOffCalculations?.totalBuildCost.value
                      ? `$${formatNumber(takeOffCalculations.totalBuildCost.value)}`
                      : 'N/A'}
                  </Typography>

                  <InfoIcon sx={{ color: '#04002E', fontSize: 16 }} />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {takeOffCalculations?.totalBuildCost.status ===
                    CostStatus.Decreased && (
                    <>
                      <DownwardArrowIcon
                        sx={{ color: '#04002E', fontSize: 12 }}
                      />

                      <Typography
                        sx={{
                          color: '#04002E',

                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: 'normal',
                        }}
                      >
                        Overall decreased from last design by
                      </Typography>

                      <Typography
                        sx={{
                          color: '#04002E',

                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: 'normal',
                          textDecorationLine: 'underline',
                        }}
                      >
                        {takeOffCalculations.totalBuildCost.percentage}%
                      </Typography>
                    </>
                  )}

                  {takeOffCalculations?.totalBuildCost.status ===
                    CostStatus.Increased && (
                    <>
                      <UpwardArrowIcon
                        sx={{ color: '#04002E', fontSize: 12 }}
                      />

                      <Typography
                        sx={{
                          color: '#04002E',

                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: 'normal',
                        }}
                      >
                        Overall increased from last design by
                      </Typography>

                      <Typography
                        sx={{
                          color: '#04002E',

                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: 'normal',
                          textDecorationLine: 'underline',
                        }}
                      >
                        {takeOffCalculations.totalBuildCost.percentage}%
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              <Divider
                flexItem
                variant='fullWidth'
                orientation='horizontal'
                sx={{
                  backgroundColor: '#D6D6D6',
                }}
              />

              <Box
                sx={{
                  gap: '10px',
                  flex: '1 1 0',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <Box
                  sx={{
                    flex: '1 1 0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '4px',
                      alignItems: 'baseline',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#04002E',

                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: 'normal',
                      }}
                    >
                      Est Total Material Cost
                    </Typography>

                    <TakeOffTooltip title={tooltipText}>
                      <InfoIcon sx={{ color: '#04002E', fontSize: 10 }} />
                    </TakeOffTooltip>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#04002E',

                        fontSize: '24px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: 'normal',
                      }}
                    >
                      {takeOffCalculations?.totalMaterialCost.value
                        ? `$${formatNumber(takeOffCalculations.totalMaterialCost.value)}`
                        : 'N/A'}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {takeOffCalculations?.totalMaterialCost.status ===
                        CostStatus.Decreased && (
                        <>
                          <DownwardArrowIcon
                            sx={{ color: '#04002E', fontSize: 12 }}
                          />

                          <Typography
                            sx={{
                              color: '#04002E',

                              fontSize: '12px',
                              fontStyle: 'normal',
                              fontWeight: 500,
                              lineHeight: 'normal',
                            }}
                          >
                            Decreased by{' '}
                            {takeOffCalculations.totalMaterialCost.percentage}%
                          </Typography>
                        </>
                      )}

                      {takeOffCalculations?.totalMaterialCost.status ===
                        CostStatus.Increased && (
                        <>
                          <UpwardArrowIcon
                            sx={{ color: '#04002E', fontSize: 12 }}
                          />

                          <Typography
                            sx={{
                              color: '#04002E',

                              fontSize: '12px',
                              fontStyle: 'normal',
                              fontWeight: 500,
                              lineHeight: 'normal',
                            }}
                          >
                            Increased by{' '}
                            {takeOffCalculations.totalMaterialCost.percentage}%
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Divider
                  flexItem
                  variant='fullWidth'
                  orientation='vertical'
                  sx={{
                    backgroundColor: '#D6D6D6',
                  }}
                />

                <Box
                  sx={{
                    flex: '1 1 0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '4px',
                      alignItems: 'baseline',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#04002E',

                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: 'normal',
                      }}
                    >
                      Est Labor Cost
                    </Typography>

                    <TakeOffTooltip title={tooltipText}>
                      <InfoIcon sx={{ color: '#04002E', fontSize: 10 }} />
                    </TakeOffTooltip>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#04002E',

                        fontSize: '24px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: 'normal',
                      }}
                    >
                      {takeOffCalculations?.totalLaborCost.value
                        ? `$${formatNumber(takeOffCalculations.totalLaborCost.value)}`
                        : 'N/A'}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {takeOffCalculations?.totalLaborCost.status ===
                        CostStatus.Decreased && (
                        <>
                          <DownwardArrowIcon
                            sx={{ color: '#04002E', fontSize: 12 }}
                          />

                          <Typography
                            sx={{
                              color: '#04002E',

                              fontSize: '12px',
                              fontStyle: 'normal',
                              fontWeight: 500,
                              lineHeight: 'normal',
                            }}
                          >
                            Decreased by{' '}
                            {takeOffCalculations.totalLaborCost.percentage}%
                          </Typography>
                        </>
                      )}

                      {takeOffCalculations?.totalLaborCost.status ===
                        CostStatus.Increased && (
                        <>
                          <UpwardArrowIcon
                            sx={{ color: '#04002E', fontSize: 12 }}
                          />

                          <Typography
                            sx={{
                              color: '#04002E',

                              fontSize: '12px',
                              fontStyle: 'normal',
                              fontWeight: 500,
                              lineHeight: 'normal',
                            }}
                          >
                            Increased by{' '}
                            {takeOffCalculations.totalLaborCost.percentage}%
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <Divider
            flexItem
            variant='fullWidth'
            orientation='vertical'
            sx={{
              backgroundColor: '#D6D6D6',
              marginY: '12px',
            }}
          />
        </Can>
      </Box>
    </Box>
  )
}

export default ProjectCost
